import React, { JSX, useRef, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { BoardState, Position } from '../model/BoardState';
import Pawn from '../piece/Pawn';
import Rook from "../piece/Rook";
import Knight from "../piece/Knight";
import Bishop from "../piece/Bishop";
import Queen from "../piece/Queen";
import King from "../piece/King";
import { Piece } from "../model/Piece";
import logger from "../utils/Logger";

interface ChessboardProps {
  boardSize?: number;
  boardState: BoardState;
  handlePlayerMoveCallback: (from: Position, to: Position) => void;
  highlightedSquare: Position | null;
  highlightedComputerSquare: Position | null;
  playerColor: { white: string; black: string };
  currentTurn: 'Player' | 'Computer';
  highlightedLegalMoves: Position[];
  setHighlightedLegalMoves: (positions: Position[]) => void;
  isGameOver: boolean;
  orientation?: 'portrait' | 'landscape';
}

let triggerComputerMoveAnimation: (from: Position, to: Position) => Promise<void>;

const MobileStandardChessBoard: React.FC<ChessboardProps> = ({
  boardSize = 1024,
  boardState,
  handlePlayerMoveCallback,
  highlightedSquare,
  highlightedComputerSquare,
  playerColor,
  currentTurn,
  highlightedLegalMoves,
  setHighlightedLegalMoves,
  isGameOver,
  orientation: _orientation = 'portrait',
}) => {
  const [draggedPiece, setDraggedPiece] = useState<Position | null>(null);
  const [animatedPiece, setAnimatedPiece] = useState<{ piece: JSX.Element; from: Position; to: Position } | null>(null);
  const [touchDragPosition, setTouchDragPosition] = useState<{x: number, y: number} | null>(null);
  const [lastTouchPosition, setLastTouchPosition] = useState<Position | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const squareSize = boardSize / 8;

  const animatePiece = (from: Position, to: Position, duration: number) => {
    // Find the piece at the 'from' position in the board state
    const piece = boardState.pieces.find(p => p.position.row === from.row && p.position.col === from.col);
    if (!piece) {
      logger.error('Piece not found for:', JSON.stringify(from));
      return;
    }

    // Create a JSX element representing the piece to be animated
    const pieceElement = (
      <div
        style={{
          position: 'absolute',
          width: squareSize,
          height: squareSize,
          left: from.col * squareSize,
          top: from.row * squareSize,
          color: piece.color === 'white' ? playerColor.white : playerColor.black,
          transition: `left ${duration}ms linear, top ${duration}ms linear`,
        }}
      >
        {piece.type === 'pawn' && <Pawn pieceColor={piece.color} />}
        {piece.type === 'rook' && <Rook pieceColor={piece.color} />}
        {piece.type === 'knight' && <Knight pieceColor={piece.color} />}
        {piece.type === 'bishop' && <Bishop pieceColor={piece.color} />}
        {piece.type === 'queen' && <Queen pieceColor={piece.color} />}
        {piece.type === 'king' && <King pieceColor={piece.color} />}
      </div>
    );

    setAnimatedPiece({ piece: pieceElement, from, to });

    setTimeout(() => {
      setAnimatedPiece(prev => {
        if (!prev) return null;
        return {
          ...prev,
          piece: React.cloneElement(prev.piece, {
            style: {
              ...prev.piece.props.style,
              left: to.col * squareSize,
              top: to.row * squareSize,
            },
          }),
        };
      });
    }, 50);

    setTimeout(() => {
      setAnimatedPiece(null);
    }, duration);
  };

  triggerComputerMoveAnimation = (from: Position, to: Position): Promise<void> => {
    return new Promise(resolve => {
      const duration = 1000;
      animatePiece(from, to, duration);
      setTimeout(() => {
        resolve();
      }, duration);
    });
  };

  const handleDragStart = (position: Position) => {
    if (isGameOver || currentTurn !== 'Player') {
      return;
    }
    const piece = boardState.pieces.find(p => p.position.row === position.row && p.position.col === position.col);
    if (piece && piece.color === 'black') {
      setDraggedPiece(position);
      const legalMoves = boardState.getLegalMoves(piece);
      setHighlightedLegalMoves(legalMoves);
    }
  };

  const handleDrop = (position: Position) => {
    if (draggedPiece) {
      const piece = boardState.pieces.find(p => p.position.row === draggedPiece.row && p.position.col === draggedPiece.col);
      if (piece) {
        const legalMoves = boardState.getLegalMoves(piece);
        if (legalMoves.some(move => move.row === position.row && move.col === position.col)) {
          handlePlayerMoveCallback(draggedPiece, position);
          const beep = new Audio('/sounds/chess-move-click.wav');
          beep.play().catch(error => logger.error('Audio playback failed:', error));
        }
      }
      setDraggedPiece(null);
      setHighlightedLegalMoves([]);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleMouseDown = (position: Position) => {
    if (isGameOver || currentTurn !== 'Player') {
      return;
    }
    const piece = boardState.pieces.find(p => p.position.row === position.row && p.position.col === position.col);
    if (piece && piece.color === 'black') {
      setDraggedPiece(position);
      const legalMoves = boardState.getLegalMoves(piece);
      setHighlightedLegalMoves(legalMoves);
    }
  };

  const handleMouseUp = (position: Position) => {
    if (draggedPiece) {
      handleDrop(position);
    }
    setHighlightedLegalMoves([]);
  };

  const handleTouchStart = useCallback((position: Position) => {
    if (isGameOver || currentTurn !== 'Player') {
      return;
    }
    
    // Get the piece at the touched position
    const piece = boardState.pieces.find(p => p.position.row === position.row && p.position.col === position.col);
    
    if (piece && piece.color === 'black') {
      // Set draggedPiece immediately to match desktop behavior
      setDraggedPiece(position);
      // Show legal moves immediately
      const legalMoves = boardState.getLegalMoves(piece);
      setHighlightedLegalMoves(legalMoves);
      // Set touch drag position
      setTouchDragPosition({ x: position.col * squareSize, y: position.row * squareSize });
      // Set last touch position
      setLastTouchPosition(position);
    } else {
      // If touching an empty square or opponent's piece, and we have a piece "in hand"
      if (draggedPiece) {
        handleDrop(position);
      }
    }
  }, [boardState, currentTurn, draggedPiece, handleDrop, isGameOver, setHighlightedLegalMoves, squareSize]);

  const handleTouchEnd = useCallback(() => {
    // If we're in the middle of a drag, finish it
    if (draggedPiece && lastTouchPosition) {
      handleDrop(lastTouchPosition);
    }
    setTouchDragPosition(null);
    setLastTouchPosition(null);
  }, [draggedPiece, handleDrop, lastTouchPosition]);

  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    // Prevent default to stop scrolling while dragging
    event.preventDefault();
    
    // Only handle touch move if we're dragging a piece
    if (draggedPiece) {
      // Get the touch position
      const touch = event.touches[0];
      const boardRect = boardRef.current?.getBoundingClientRect();
      
      if (boardRect) {
        // Get the chess grid container element to calculate relative positions
        const gridElement = boardRef.current?.querySelector('div > div > div');
        const gridRect = gridElement?.getBoundingClientRect();
        
        if (gridRect) {
          // Calculate the relative position within the actual grid
          const relativeX = touch.clientX - gridRect.left;
          const relativeY = touch.clientY - gridRect.top;
          
          // Calculate cell size based on the actual rendered grid
          const cellWidth = gridRect.width / 8;
          const cellHeight = gridRect.height / 8;
          
          // Calculate which square the touch is over
          const col = Math.floor(relativeX / cellWidth);
          const row = Math.floor(relativeY / cellHeight);
          
          // Only handle valid board positions
          if (col >= 0 && col < 8 && row >= 0 && row < 8) {
            setLastTouchPosition({ row, col });
          }
          
          // Set ghost piece position directly under the finger
          // Use percentage-based positioning relative to the grid
          const ghostX = ((relativeX - (cellWidth / 2)) / gridRect.width) * 100;
          const ghostY = ((relativeY - (cellHeight / 2)) / gridRect.height) * 100;
          
          setTouchDragPosition({ 
            x: ghostX, 
            y: ghostY
          });
        }
      }
    }
  }, [draggedPiece]);

  const shouldRenderPiece = (
    piece: Piece | undefined,
    animatedPiece: { from: Position; to: Position } | null,
    row: number,
    col: number
  ): boolean => {
    if (!piece) return false;
    if (!animatedPiece) return true;

    const isAnimatingFromCell = animatedPiece.from.row === row && animatedPiece.from.col === col;
    const isAnimatingToCell = animatedPiece.to.row === row && animatedPiece.to.col === col;

    if (isAnimatingToCell && piece.color === 'black') return true;
    if (isAnimatingToCell) return false;
    return !isAnimatingFromCell;
  };

  const squares: JSX.Element[] = [];
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const isHighlighted = highlightedSquare && highlightedSquare.row === row && highlightedSquare.col === col;
      const isComputerHighlighted = highlightedComputerSquare && highlightedComputerSquare.row === row && highlightedComputerSquare.col === col;
      const isLegalMoveHighlighted = highlightedLegalMoves.some(move => move.row === row && move.col === col);
      const isLightSquare = (row + col) % 2 === 0;

      const piece = boardState.pieces.find(p => p.position.row === row && p.position.col === col);

      squares.push(
        <Box
          key={`${row}-${col}`}
          sx={{
            width: `${squareSize}px`,
            height: `${squareSize}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            boxSizing: 'border-box',
            backgroundColor: isLightSquare ? '#f0d9b5' : '#b58863',
            outlineOffset: '-1px',
            outline: isHighlighted || isComputerHighlighted 
              ? '2px solid red' 
              : isLegalMoveHighlighted 
                ? '2px dashed blue' 
                : 'none',
            border: '0',
            padding: 0,
            margin: 0,
            WebkitTouchCallout: 'none', // iOS Safari
            WebkitUserSelect: 'none',   // Safari
            KhtmlUserSelect: 'none',    // Konqueror HTML
            MozUserSelect: 'none',      // Old versions of Firefox
            msUserSelect: 'none',       // Internet Explorer/Edge
            userSelect: 'none',         // Non-prefixed version
            touchAction: 'none',        // Disable browser handling of all touch gestures
          }}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop({ row, col })}
          onMouseUp={() => handleMouseUp({ row, col })}
          onTouchStart={(e) => {
            // Prevent default to disable touch feedback/context menu
            e.preventDefault();
            handleTouchStart({ row, col });
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            handleTouchEnd();
          }}
          onTouchMove={handleTouchMove}
          onContextMenu={(e) => e.preventDefault()} // Prevent right-click/long-press context menu
        >
          {shouldRenderPiece(piece, animatedPiece, row, col) && (
            <div
              draggable={piece?.color === 'black'}
              onDragStart={() => handleDragStart({ row, col })}
              onMouseDown={() => handleMouseDown({ row, col })}
              onMouseUp={() => handleMouseUp({ row, col })}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: piece?.color === 'white' ? playerColor.white : playerColor.black,
                touchAction: 'none',
                opacity: touchDragPosition && draggedPiece && draggedPiece.row === row && draggedPiece.col === col ? 0.6 : 1,
                transform: touchDragPosition && draggedPiece && draggedPiece.row === row && draggedPiece.col === col 
                  ? 'scale(1.1)' 
                  : 'scale(1)',
                transition: 'opacity 0.1s, transform 0.1s',
              }}
              data-row={row}
              data-col={col}
              role="button"
              tabIndex={0}
              aria-label={`${piece?.color} ${piece?.type} at position ${row},${col}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleDragStart({ row, col });
                }
              }}
            >
              {piece?.type === 'pawn' && <Pawn pieceColor={piece.color} />}
              {piece?.type === 'rook' && <Rook pieceColor={piece.color} />}
              {piece?.type === 'knight' && <Knight pieceColor={piece.color} />}
              {piece?.type === 'bishop' && <Bishop pieceColor={piece.color} />}
              {piece?.type === 'queen' && <Queen pieceColor={piece.color} />}
              {piece?.type === 'king' && <King pieceColor={piece.color} />}
            </div>
          )}
        </Box>
      );
    }
  }

  return (
    <Box 
      ref={boardRef} 
      sx={{ 
        display: 'flex',
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100%', 
        width: '100%',
        position: 'relative',
        minHeight: 0, // Allow shrinking below min content size
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        position: 'relative',
        width: 'auto',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: '100%',
        aspectRatio: '1/1',
        backgroundColor: '#000', // Background color for any potential tiny gaps
      }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gridTemplateRows: 'repeat(8, 1fr)',
            gap: 0,
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            border: 'none',
          }}
        >
          {squares}
        </Box>
        {animatedPiece && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {animatedPiece.piece}
          </div>
        )}
        {touchDragPosition && draggedPiece && (
          <div
            style={{
              position: 'absolute',
              width: squareSize,
              height: squareSize,
              left: `${touchDragPosition.x}%`,
              top: `${touchDragPosition.y}%`,
              pointerEvents: 'none',
              opacity: 0.5,
            }}
          >
            {boardState.pieces.find(p => p.position.row === draggedPiece.row && p.position.col === draggedPiece.col)?.type === 'pawn' && <Pawn pieceColor="black" />}
            {boardState.pieces.find(p => p.position.row === draggedPiece.row && p.position.col === draggedPiece.col)?.type === 'rook' && <Rook pieceColor="black" />}
            {boardState.pieces.find(p => p.position.row === draggedPiece.row && p.position.col === draggedPiece.col)?.type === 'knight' && <Knight pieceColor="black" />}
            {boardState.pieces.find(p => p.position.row === draggedPiece.row && p.position.col === draggedPiece.col)?.type === 'bishop' && <Bishop pieceColor="black" />}
            {boardState.pieces.find(p => p.position.row === draggedPiece.row && p.position.col === draggedPiece.col)?.type === 'queen' && <Queen pieceColor="black" />}
            {boardState.pieces.find(p => p.position.row === draggedPiece.row && p.position.col === draggedPiece.col)?.type === 'king' && <King pieceColor="black" />}
          </div>
        )}
      </Box>
    </Box>
  );
};

export { triggerComputerMoveAnimation };
export default MobileStandardChessBoard;